import { Currency, Token } from '@kodiak-finance/sdk-core'
import { computePoolAddress, FeeAmount } from '@kodiak-finance/v3-sdk'
import { V3_FACTORY_ADDRESSES } from 'constants/addresses'
import { useMemo } from 'react'

import { useFeeTierDistributionByIds, useV3PoolsByAddresses } from './subgraph/v3'
import { useConnection } from './useConnection'

const FEES: FeeAmount[] = [FeeAmount.LOWEST, FeeAmount.LOW, FeeAmount.MEDIUM, FeeAmount.HIGH, FeeAmount.EXTRA]

type DistRet = {
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  distributions?: Record<FeeAmount, number | undefined>
  tvlAmounts?: Record<FeeAmount, number | undefined>
}

interface FeeTierDistribution {
  isLoading: boolean
  isError: boolean
  largestUsageFeeTier?: FeeAmount | undefined
  tvlAmounts?: Record<FeeAmount, number | undefined>
  distributions?: Record<FeeAmount, number | undefined>
}

export function useFeeTierDistribution(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined
): FeeTierDistribution {
  const { chainId } = useConnection()

  const poolAddresses = useMemo(() => {
    if (!currencyA || !currencyB || !chainId) return []

    const tokenA = currencyA.wrapped
    const tokenB = currencyB.wrapped
    const factoryAddress = V3_FACTORY_ADDRESSES[chainId]

    if (!factoryAddress) return []

    return [FeeAmount.LOWEST, FeeAmount.LOW, FeeAmount.MEDIUM, FeeAmount.HIGH, FeeAmount.EXTRA].map((fee) =>
      computePoolAddress({
        factoryAddress,
        tokenA: tokenB,
        tokenB: tokenA,
        fee,
      }).toLowerCase()
    )
  }, [currencyA, currencyB, chainId])

  const { data: poolsData, isLoading: poolsLoading, isError: poolsError } = useV3PoolsByAddresses(poolAddresses)

  const { isFetching, isLoading, isUninitialized, isError, distributions, tvlAmounts } = usePoolTVL(
    currencyA?.wrapped,
    currencyB?.wrapped
  )

  return useMemo(() => {
    if (isLoading || isFetching || isUninitialized || isError || poolsLoading) {
      return {
        isLoading: isLoading || isFetching || poolsLoading,
        isError: isError || poolsError,
        distributions,
        tvlAmounts,
      }
    }

    if (!distributions) {
      return {
        isLoading: isLoading || isFetching || poolsLoading,
        isError: isError || poolsError,
        distributions,
      }
    }

    const largestUsageFeeTier = Object.keys(distributions)
      .map((d) => Number(d))
      .filter((d: FeeAmount) => distributions[d] !== 0 && distributions[d] !== undefined)
      .reduce((a: FeeAmount, b: FeeAmount) => ((distributions[a] ?? 0) > (distributions[b] ?? 0) ? a : b), -1)

    // Create pool existence map from subgraph data
    const poolsById = (poolsData?.pools || []).reduce((acc: { [x: string]: any }, pool: { id: string }) => {
      acc[pool.id.toLowerCase()] = pool
      return acc
    }, {} as Record<string, any>)

    const percentages = {
      [FeeAmount.LOWEST]: poolsById[poolAddresses[0]] ? (distributions[FeeAmount.LOWEST] ?? 0) * 100 : undefined,
      [FeeAmount.LOW]: poolsById[poolAddresses[1]] ? (distributions[FeeAmount.LOW] ?? 0) * 100 : undefined,
      [FeeAmount.MEDIUM]: poolsById[poolAddresses[2]] ? (distributions[FeeAmount.MEDIUM] ?? 0) * 100 : undefined,
      [FeeAmount.HIGH]: poolsById[poolAddresses[3]] ? (distributions[FeeAmount.HIGH] ?? 0) * 100 : undefined,
      [FeeAmount.EXTRA]: poolsById[poolAddresses[4]] ? (distributions[FeeAmount.EXTRA] ?? 0) * 100 : undefined,
    }

    return {
      isLoading,
      isError,
      distributions: percentages,
      largestUsageFeeTier: largestUsageFeeTier === -1 ? undefined : largestUsageFeeTier,
      tvlAmounts,
    }
  }, [
    isLoading,
    isFetching,
    isUninitialized,
    isError,
    distributions,
    poolsLoading,
    poolsData,
    poolAddresses,
    poolsError,
    tvlAmounts,
  ])
}

function usePoolTVL(token0: Token | undefined, token1: Token | undefined) {
  const { chainId } = useConnection()
  const ids = useMemo(() => {
    if (!token0 || !token1 || !chainId) return []
    const factoryAddress = V3_FACTORY_ADDRESSES[chainId]
    if (!factoryAddress) return []

    const a = token0
    const b = token1
    // order like your existing computePoolAddress usage (note you flipped A/B there)
    return FEES.map((fee) =>
      computePoolAddress({
        factoryAddress,
        tokenA: b,
        tokenB: a,
        fee,
      }).toLowerCase()
    )
  }, [token0, token1, chainId])

  const { data, isLoading, isFetching, isError } = useFeeTierDistributionByIds(ids)
  const isUninitialized = !ids.length || (!data && !isLoading && !isFetching)

  const pools = useMemo(() => data?.pools ?? [], [data?.pools])

  return useMemo(() => {
    // No data yet -> just surface the flags
    if (!pools.length) {
      return { isLoading, isFetching, isUninitialized, isError } as DistRet
    }

    // Accumulator: per feeTier, [tvl0, tvl1]
    const tvlByFeeTier = FEES.reduce<Record<number, [number | undefined, number | undefined]>>((acc, f) => {
      acc[f] = [undefined, undefined]
      return acc
    }, {})

    for (const p of pools) {
      const f: FeeAmount = Number(p.feeTier) as FeeAmount
      if (!(f in tvlByFeeTier)) continue
      const prev0 = tvlByFeeTier[f][0] ?? 0
      const prev1 = tvlByFeeTier[f][1] ?? 0
      tvlByFeeTier[f][0] = prev0 + Number(p.totalValueLockedToken0 ?? 0)
      tvlByFeeTier[f][1] = prev1 + Number(p.totalValueLockedToken1 ?? 0)
    }

    // Totals across all fee tiers
    let sumToken0Tvl = 0
    let sumToken1Tvl = 0
    for (const [t0, t1] of Object.values(tvlByFeeTier)) {
      sumToken0Tvl += t0 ?? 0
      sumToken1Tvl += t1 ?? 0
    }
    const denom = sumToken0Tvl + sumToken1Tvl

    // Helper: undefined if both undefined, else (t0+t1)/total
    const mean = (
      tvl0: number | undefined,
      sumTvl0: number,
      tvl1: number | undefined,
      sumTvl1: number
    ): number | undefined => {
      const bothUndef = tvl0 === undefined && tvl1 === undefined
      if (bothUndef) return undefined
      const num = (tvl0 ?? 0) + (tvl1 ?? 0)
      const den = sumTvl0 + sumTvl1
      return den ? num / den : 0
    }

    const distributions: Record<FeeAmount, number | undefined> = {
      [FeeAmount.LOWEST]: mean(
        tvlByFeeTier[FeeAmount.LOWEST][0],
        sumToken0Tvl,
        tvlByFeeTier[FeeAmount.LOWEST][1],
        sumToken1Tvl
      ),
      [FeeAmount.LOW]: mean(tvlByFeeTier[FeeAmount.LOW][0], sumToken0Tvl, tvlByFeeTier[FeeAmount.LOW][1], sumToken1Tvl),
      [FeeAmount.MEDIUM]: mean(
        tvlByFeeTier[FeeAmount.MEDIUM][0],
        sumToken0Tvl,
        tvlByFeeTier[FeeAmount.MEDIUM][1],
        sumToken1Tvl
      ),
      [FeeAmount.HIGH]: mean(
        tvlByFeeTier[FeeAmount.HIGH][0],
        sumToken0Tvl,
        tvlByFeeTier[FeeAmount.HIGH][1],
        sumToken1Tvl
      ),
      [FeeAmount.EXTRA]: mean(
        tvlByFeeTier[FeeAmount.EXTRA][0],
        sumToken0Tvl,
        tvlByFeeTier[FeeAmount.EXTRA][1],
        sumToken1Tvl
      ),
    }

    const tvlAmounts: Record<FeeAmount, number | undefined> = {
      [FeeAmount.LOWEST]:
        tvlByFeeTier[FeeAmount.LOWEST][0] !== undefined || tvlByFeeTier[FeeAmount.LOWEST][1] !== undefined
          ? (tvlByFeeTier[FeeAmount.LOWEST][0] ?? 0) + (tvlByFeeTier[FeeAmount.LOWEST][1] ?? 0)
          : undefined,
      [FeeAmount.LOW]:
        tvlByFeeTier[FeeAmount.LOW][0] !== undefined || tvlByFeeTier[FeeAmount.LOW][1] !== undefined
          ? (tvlByFeeTier[FeeAmount.LOW][0] ?? 0) + (tvlByFeeTier[FeeAmount.LOW][1] ?? 0)
          : undefined,
      [FeeAmount.MEDIUM]:
        tvlByFeeTier[FeeAmount.MEDIUM][0] !== undefined || tvlByFeeTier[FeeAmount.MEDIUM][1] !== undefined
          ? (tvlByFeeTier[FeeAmount.MEDIUM][0] ?? 0) + (tvlByFeeTier[FeeAmount.MEDIUM][1] ?? 0)
          : undefined,
      [FeeAmount.HIGH]:
        tvlByFeeTier[FeeAmount.HIGH][0] !== undefined || tvlByFeeTier[FeeAmount.HIGH][1] !== undefined
          ? (tvlByFeeTier[FeeAmount.HIGH][0] ?? 0) + (tvlByFeeTier[FeeAmount.HIGH][1] ?? 0)
          : undefined,
      [FeeAmount.EXTRA]:
        tvlByFeeTier[FeeAmount.EXTRA][0] !== undefined || tvlByFeeTier[FeeAmount.EXTRA][1] !== undefined
          ? (tvlByFeeTier[FeeAmount.EXTRA][0] ?? 0) + (tvlByFeeTier[FeeAmount.EXTRA][1] ?? 0)
          : undefined,
    }

    return {
      isLoading,
      isFetching,
      isUninitialized,
      isError,
      distributions,
      tvlAmounts,
    } as DistRet
  }, [pools, isLoading, isFetching, isUninitialized, isError])
}
